import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { differenceInDays } from 'date-fns';
import { Chip, cn, Popover } from '@c2fo/liquidity';
import {
  ArrowRightFromLineDuotoneIcon,
  ArrowsRotateLightIcon,
  BellExclamationDuotoneIcon,
  BoltLightningLightIcon,
  CalendarCheckLightIcon,
  CalendarLinesLightIcon,
  GemLightIcon,
  InfoCircleIcon,
  ShapesDuotoneIcon,
} from '@c2fo/liquidity/icons';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import RateDisplay from '@/enterprise/features/homepage/marketTable/RateDisplay';
import { buildCustomScheduleSentenceFromExistingOfferSchedule } from '@/enterprise/features/offerModal/utils';
import ParticipationSwitch from '@/enterprise/features/participationSwitch/ParticipationSwitch';
import RenewalRequestIcon from '@/enterprise/features/preferred/RenewalRequestIcon';
import useGetRecurringRulesForTakerMarkets from '@/enterprise/features/recurringRules/utils/useGetRecurringRulesForTakerMarkets';
import { useMarketStatsStatus } from '@/lib/useMarketStatsStatus';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import getRenewalSchedule from '@/utils/getRenewalSchedule';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import { parseIsoDateStringToDateCorrectTz } from '@/utils/parseIsoDateStringToDate';
import { isPreferredOfferNearTermEnd } from '@/utils/preferredOfferRenewal';
import useIsNewDivision from '@/utils/useIsNewDivision';
import useLocaleFormat from '@/utils/useLocaleFormat';
import usePricingTypeDisplay from '@/utils/usePricingTypeDisplay';
import ClosedMarketChip from '../ClosedMarketChip';
import { getCommonPinningStyles, rightShadowClassNames, usePinnedColumnShadow } from '../DataTable/tablePinning';
import { getNewDivisionAlertEligibleTakerMarkets, useEnableNewDivisionAlert } from '../NewDivisionAlert';
import NextMarketClearTimeCountdown from '../NextMarketClearTimeCountdown';
import AcceptanceTooltip from './AcceptanceTooltip';
import DivisionTableActionsDropdown from './DivisionTableActionsDropdown';

const DivisionTable = ({ takerMarkets }: { takerMarkets: TakerMarket[] }) => {
  const { t } = useTranslation();
  const { isNewDivision } = useIsNewDivision();
  const { getPricingTypeDisplay } = usePricingTypeDisplay();
  const { listenToMarketStats } = useMarketStatsStatus();
  const { asCurrency, asDateStringText, asNumber, asPercent } = useLocaleFormat();
  const { getRecurringRulesForTakerMarkets } = useGetRecurringRulesForTakerMarkets();
  const { tableRef, showRightPinnedShadow } = usePinnedColumnShadow({ left: false });
  const enableNewDivisionAlert = useEnableNewDivisionAlert();
  const getNewDivisionAlertEligible = enableNewDivisionAlert ? getNewDivisionAlertEligibleTakerMarkets : undefined;

  // Check if a specific market is listening for stat updates
  const getIsListeningForMarket = useCallback(
    (tm: TakerMarket): boolean => {
      return (
        listenToMarketStats([
          {
            marketUuid: tm.marketUuid,
            takerId: tm.takerDivisionId,
            takerDivisionUuid: tm.takerDivisionUuid,
            makerOrganizationUuid: tm.makerOrganizationUuid,
          },
        ]) ?? false
      );
    },
    [listenToMarketStats]
  );

  const isOfferInactive = (tm: TakerMarket) => !tm.offerConfig.isEnabled && !tm.offerSchedule;

  // invoice rules lookup
  const takerMarketsWithRulesLookup = useMemo(() => {
    const allRules = getRecurringRulesForTakerMarkets(takerMarkets);
    const lookup = new Set<string>();
    allRules.forEach((rule) => {
      const key = `${rule.makerOrganizationUuid}-${rule.marketId}-${rule.takerId}`;
      lookup.add(key);
    });
    return lookup;
  }, [takerMarkets, getRecurringRulesForTakerMarkets]);

  const columns = useMemo<ColumnDef<TakerMarket>[]>(() => {
    const getOfferDetailsTag = (tm: TakerMarket) => {
      if (tm.offerConfig.isEnabled && tm.offerConfig.offerType === 'PREFERRED_TERM') {
        const { hasRenewalSchedule, renewalIntervalDays } = getRenewalSchedule(tm);

        if (hasRenewalSchedule && tm.disableAfterMarketClearsDate) {
          return (
            <span className="flex items-center gap-x-1 text-nowrap">
              <GemLightIcon className="size-4" />
              {t('core.preferredOfferDetails')} -{' '}
              {asDateStringText({ date: tm.disableAfterMarketClearsDate, isUTC: false })}
              <Popover>
                <Popover.Trigger aria-label={t('offer.autoRenew')}>
                  <InfoCircleIcon className="size-3 fill-text-secondary" />
                </Popover.Trigger>
                <Popover.Content arrow collisionPadding={24}>
                  <div className="max-w-60 text-center">
                    <Trans
                      i18nKey="offer.autoRenew.modal.disable.info"
                      values={{
                        renewalIntervalDays,
                        disableAfterMarketClearsDate: asDateStringText({
                          date: tm.disableAfterMarketClearsDate,
                          isUTC: false,
                          formatOptions: { month: 'long' },
                        }),
                      }}
                      components={{ bold: <strong /> }}
                    />
                  </div>
                </Popover.Content>
              </Popover>
            </span>
          );
        }

        if (tm.disableAfterMarketClearsDate) {
          const daysUntilExpiry = differenceInDays(
            parseIsoDateStringToDateCorrectTz(tm.disableAfterMarketClearsDate),
            new Date()
          );
          const isNearTermEnd = isPreferredOfferNearTermEnd(tm);

          if (isNearTermEnd) {
            return (
              <span className="flex items-center gap-x-1 text-nowrap">
                <GemLightIcon className="size-4" />
                {t('core.preferredOfferDetails')} -{' '}
                <span className="font-medium text-red-700">{t('core.expiringInDays', { count: daysUntilExpiry })}</span>
                <RenewalRequestIcon iconStyles="size-4 fill-red-700" iconType="duotone" takerMarket={tm} />
              </span>
            );
          }

          return (
            <span className="flex items-center gap-x-1 text-nowrap">
              <GemLightIcon className="size-4" />
              {t('core.preferredOfferDetails')} -{' '}
              {asDateStringText({ date: tm.disableAfterMarketClearsDate, isUTC: false })}
            </span>
          );
        }

        return (
          <span className="flex items-center gap-x-1 text-nowrap">
            <GemLightIcon className="size-4" />
            {t('core.preferredOfferDetails')}
          </span>
        );
      }

      if (tm.offerSchedule) {
        const toolTipText = buildCustomScheduleSentenceFromExistingOfferSchedule({
          offerSchedule: tm.offerSchedule,
          t,
        });
        return (
          <span className="flex items-center gap-x-1 text-nowrap">
            <CalendarLinesLightIcon className="size-4" /> {t('core.scheduledOfferDetails')}
            <Popover>
              <Popover.Trigger aria-label={t('core.scheduledOfferDetails')}>
                <InfoCircleIcon className="size-3 fill-text-secondary" />
              </Popover.Trigger>
              <Popover.Content arrow collisionPadding={24}>
                <div className="max-w-60 text-center">{toolTipText}</div>
              </Popover.Content>
            </Popover>
          </span>
        );
      }

      if (tm.offerConfig.frequency === 'ONE_TIME') {
        if (tm.offerConfig.offerType === 'EXPRESS_ACCEPT') {
          return (
            <span className="flex items-center gap-x-1 text-nowrap">
              <BoltLightningLightIcon className="size-4" /> {t('core.oneTimeExpressAcceptOfferDetails')}
            </span>
          );
        }

        return (
          <span className="flex items-center gap-x-1 text-nowrap">
            <CalendarCheckLightIcon className="size-4" /> {t('core.oneTimeOfferDetails')}
          </span>
        );
      }

      return (
        <span className="flex items-center gap-x-1 text-nowrap">
          <ArrowsRotateLightIcon className="size-4" /> {t('core.recurringOfferDetails')}
        </span>
      );
    };

    return [
      {
        id: 'participation',
        header: () => t('core.participation'),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <ParticipationSwitch takerMarket={row.original} />
          </div>
        ),
      },
      {
        id: 'division',
        header: () => t('core.division'),
        cell: ({ row }) => {
          const tm = row.original;
          const { hasRenewalSchedule, renewalIntervalDays } = getRenewalSchedule(tm);
          const isPreferredWithAutoRenew =
            tm.offerConfig.isEnabled && tm.offerConfig.offerType === 'PREFERRED_TERM' && hasRenewalSchedule;
          return (
            <>
              <div className="flex items-center gap-x-1 text-nowrap">
                {(isNewDivision(tm.userAddedToDivision) || getNewDivisionAlertEligible?.([tm])?.includes(tm)) && (
                  <Chip label={t('core.new')} isRound size="xs" type="info" variant="filled" />
                )}
                <Link
                  className="font-medium text-blue-500"
                  to={asEnterpriseLink(
                    `customers/${tm.makerOrganizationUuid}/markets/${tm.marketId}/division/${tm.takerDivisionId}/invoices/eligible`
                  )}
                  title={getTakerMarketName(tm)}
                >
                  {getTakerMarketName(tm)}
                </Link>
              </div>
              <div className="text-sm" title={getTakerMarketDivisionTitle(tm)?.title}>
                {getTakerMarketDivisionTitle(tm)?.content}
              </div>
              {!tm.marketIsEnabled ? (
                <ClosedMarketChip />
              ) : isPreferredWithAutoRenew ? (
                <div className="flex items-center gap-x-1 text-sm text-text-secondary">
                  {t('core.preferredRate')} - {t('offer.autoRenew')}
                  <Popover>
                    <Popover.Trigger aria-label={t('offer.autoRenew')}>
                      <InfoCircleIcon className="size-3 fill-text-secondary" />
                    </Popover.Trigger>
                    <Popover.Content arrow className="max-w-[240px] text-center" collisionPadding={24}>
                      {tm.disableAfterMarketClearsDate && (
                        <Trans
                          i18nKey="offer.autoRenew.modal.disable.info"
                          values={{
                            renewalIntervalDays,
                            disableAfterMarketClearsDate: asDateStringText({
                              date: tm.disableAfterMarketClearsDate,
                              isUTC: false,
                              formatOptions: { month: 'long' },
                            }),
                          }}
                          components={{ bold: <strong /> }}
                        />
                      )}
                    </Popover.Content>
                  </Popover>
                </div>
              ) : (
                <div className="text-sm text-text-secondary">{getPricingTypeDisplay(tm)}</div>
              )}
              <div className="flex items-center gap-1 text-sm text-text-secondary">
                <div>{tm.currency}</div>
                <NextMarketClearTimeCountdown asIcon takerMarket={tm} />
                {takerMarketsWithRulesLookup.has(
                  `${tm.makerOrganizationUuid}-${tm.legacyMarketId}-${tm.takerDivisionId}`
                ) ? (
                  <Popover>
                    <Popover.Trigger aria-label={t('core.invoicesManagedByInvoiceRules')}>
                      <ShapesDuotoneIcon className="size-4 fill-blue-700" />
                    </Popover.Trigger>
                    <Popover.Content arrow collisionPadding={24}>
                      <div className="w-44 text-center">{t('core.invoicesManagedByInvoiceRules')}</div>
                    </Popover.Content>
                  </Popover>
                ) : tm.takerExcludedInvoiceAmount > 0 ? (
                  <Popover>
                    <Popover.Trigger aria-label={t('core.someInvoicesManuallyExcluded')}>
                      <ArrowRightFromLineDuotoneIcon className="size-4 fill-blue-700" />
                    </Popover.Trigger>
                    <Popover.Content arrow collisionPadding={24}>
                      <div className="w-44 text-center">{t('core.someInvoicesManuallyExcluded')}</div>
                    </Popover.Content>
                  </Popover>
                ) : null}
              </div>
            </>
          );
        },
      },
      {
        id: 'totalAmount',
        header: () => t('core.totalAmount'),
        cell: ({ row }) => {
          const tm = row.original;
          return asCurrency(tm.totalInvoiceAmount, tm.currency);
        },
      },
      {
        id: 'acceptedAmount',
        header: () => t('core.estimatedAcceptanceAmount'),
        cell: ({ row }) => {
          const tm = row.original;
          const isListening = getIsListeningForMarket(tm);
          return (
            <>
              {tm.offerConfig.isEnabled || tm.offerSchedule ? (
                <div className="absolute left-4 top-5 z-[1] text-xs text-text-secondary">{getOfferDetailsTag(tm)}</div>
              ) : null}
              <div
                className={cn('flex h-24 flex-col items-end justify-center rounded-l-lg bg-canvas px-4 pt-6', {
                  'h-20 border-y border-l border-stroke bg-white pt-0': isOfferInactive(tm),
                  'blur-sm': isListening,
                })}
              >
                <span className="text-nowrap">{asCurrency(tm.clearingInvoiceAmount, tm.currency)} /</span>
                <span className="text-text-secondary">
                  {asCurrency(tm.eligibleInvoiceAmount + tm.awardedInvoiceAmount, tm.currency)}
                </span>
              </div>
            </>
          );
        },
      },
      {
        id: 'acceptance',
        header: () => t('core.estimatedAcceptance'),
        cell: ({ row }) => {
          const tm = row.original;
          const isListening = getIsListeningForMarket(tm);
          const showAcceptanceTooltip =
            tm.offerConfig.isEnabled && tm.eligibleInvoiceCount + tm.awardedInvoiceCount > 0;
          return (
            <div
              className={cn('flex h-24 flex-col items-center justify-center bg-canvas px-4 pt-6', {
                'h-20 border-y border-stroke bg-white pt-0': isOfferInactive(tm),
                'blur-sm': isListening,
              })}
            >
              {showAcceptanceTooltip ? (
                <>
                  {tm.acceptancePercentage < 0.9 ? (
                    <AcceptanceTooltip>
                      <span className="flex items-center gap-1 font-medium text-red-700">
                        <BellExclamationDuotoneIcon className="size-5 fill-red-700" />
                        {asPercent({ value: tm.acceptancePercentage })}
                      </span>
                    </AcceptanceTooltip>
                  ) : (
                    <span className="flex items-center gap-1 font-medium text-green-700">
                      {asPercent({ value: tm.acceptancePercentage })}
                    </span>
                  )}
                </>
              ) : (
                <span>--</span>
              )}
            </div>
          );
        },
      },
      {
        id: 'rate',
        header: () => t('core.rate'),
        cell: ({ row }) => {
          const tm = row.original;
          const isListening = getIsListeningForMarket(tm);
          return (
            <div
              className={cn('flex h-24 flex-col items-end justify-center bg-canvas px-4 pt-6', {
                'h-20 border-y border-stroke bg-white pt-0': isOfferInactive(tm),
                'blur-sm': isListening,
              })}
            >
              <span className="text-nowrap">
                <RateDisplay takerMarket={tm} />
              </span>
            </div>
          );
        },
      },
      {
        id: 'invoices',
        header: () => t('maker.invoices'),
        cell: ({ row }) => {
          const tm = row.original;
          const isListening = getIsListeningForMarket(tm);
          return (
            <div
              className={cn('flex h-24 flex-col justify-center bg-canvas px-4 pt-6', {
                'h-20 border-y border-stroke bg-white pt-0': isOfferInactive(tm),
                'blur-sm': isListening,
              })}
            >
              <span className="text-nowrap">
                {asNumber({ value: tm.eligibleInvoiceCount + tm.awardedInvoiceCount })} /{' '}
                {asNumber({ value: tm.totalInvoiceCount })}
              </span>
            </div>
          );
        },
      },
      {
        id: 'estimatedDepositDate',
        header: () => t('draftOffer.labels.estimatedDepositDate'),
        cell: ({ row }) => {
          const tm = row.original;
          const isListening = getIsListeningForMarket(tm);
          return (
            <div
              className={cn(
                'flex h-24 flex-col items-end justify-center text-nowrap rounded-r-lg bg-canvas px-4 pt-6',
                {
                  'h-20 border-y border-r border-stroke bg-white pt-0': isOfferInactive(tm),
                  'blur-sm': isListening,
                }
              )}
            >
              <span>{tm.marketGoodFundsDate ? asDateStringText({ date: tm.marketGoodFundsDate }) : '--'}</span>
              <span className="text-nowrap text-text-secondary">
                {tm.clearingDpeWeightedAvg > 0
                  ? t('core.daysPaidEarlyCount', {
                      formattedCount: asNumber({ value: tm.clearingDpeWeightedAvg }),
                      count: tm.clearingDpeWeightedAvg,
                    })
                  : '--'}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: () => '',
        cell: ({ row }) => <DivisionTableActionsDropdown takerMarket={row.original} />,
        size: 60,
      },
    ];
  }, [
    t,
    asCurrency,
    asDateStringText,
    asNumber,
    asPercent,
    getPricingTypeDisplay,
    isNewDivision,
    takerMarketsWithRulesLookup,
    getNewDivisionAlertEligible,
    getIsListeningForMarket,
  ]);

  const table = useReactTable({
    data: takerMarkets,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    enableFilters: false,
    enableColumnResizing: false,
    enableRowSelection: false,
    initialState: {
      columnPinning: {
        right: ['actions'],
      },
    },
  });

  return (
    <div ref={tableRef} className="w-full overflow-auto border-t border-stroke">
      <table className="w-full border-collapse">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b border-stroke text-sm text-deepBlue-900">
              {headerGroup.headers.map((header) => {
                const isParticipationCol = header.column.id === 'participation';
                const isDivisionCol = header.column.id === 'division';
                const isTotalAmountCol = header.column.id === 'totalAmount';

                return (
                  <th
                    key={header.id}
                    className={cn('bg-canvas p-4 font-normal', {
                      'text-center':
                        isParticipationCol || header.column.id === 'acceptance' || header.column.id === 'invoices',
                      'w-80 border-l border-stroke bg-gray-100 text-left font-medium': isDivisionCol,
                      'border-r border-stroke bg-gray-100 text-right font-medium': isTotalAmountCol,
                      'text-right':
                        header.column.id === 'acceptedAmount' ||
                        header.column.id === 'rate' ||
                        header.column.id === 'estimatedDepositDate',
                      [rightShadowClassNames]: header.column.getIsFirstColumn('right') && showRightPinnedShadow,
                    })}
                    style={getCommonPinningStyles(header.column)}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const tm = row.original;
            const isNew = isNewDivision(tm.userAddedToDivision) || getNewDivisionAlertEligible?.([tm]).includes(tm);

            return (
              <tr key={row.id} className="border-b border-stroke last:border-b-0">
                {row.getVisibleCells().map((cell) => {
                  const isParticipationCol = cell.column.id === 'participation';
                  const isDivisionCol = cell.column.id === 'division';
                  const isTotalAmountCol = cell.column.id === 'totalAmount';
                  const isAcceptedAmountCol = cell.column.id === 'acceptedAmount';
                  const isAcceptanceCol = cell.column.id === 'acceptance';
                  const isRateCol = cell.column.id === 'rate';
                  const isInvoicesCol = cell.column.id === 'invoices';
                  const isEstimatedDepositDateCol = cell.column.id === 'estimatedDepositDate';
                  const isActionsCol = cell.column.id === 'actions';

                  return (
                    <td
                      key={cell.id}
                      className={cn('h-28', {
                        'shadow-[inset_4px_0_0_0_rgb(54,157,217)]': isParticipationCol && isNew,
                        'px-4': isParticipationCol || isDivisionCol || isTotalAmountCol || isActionsCol,
                        'w-80 truncate': isDivisionCol,
                        'text-right text-sm': isTotalAmountCol,
                        'relative p-0 text-right text-sm': isAcceptedAmountCol,
                        'p-0 text-sm': isAcceptanceCol || isRateCol || isInvoicesCol || isEstimatedDepositDateCol,
                        'text-right': isRateCol || isEstimatedDepositDateCol,
                        'text-center': isInvoicesCol,
                        'bg-white': cell.column.getIsPinned() === 'right',
                        [rightShadowClassNames]: cell.column.getIsFirstColumn('right') && showRightPinnedShadow,
                      })}
                      style={getCommonPinningStyles(cell.column)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DivisionTable;
